/**
 * ProgressIndicator component
 * Shows the current step in the multi-step application form
 * Uses theme tokens for branded step colors
 */
import React from 'react'
import { useTranslation } from '../../hooks/useTranslation'

interface ProgressIndicatorProps {
  currentStep: number
  totalSteps?: number
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  currentStep,
  totalSteps = 3,
}) => {
  const { t } = useTranslation()

  return (
    <div className="w-full">
      {/* Step text */}
      <div className="text-center mb-4 text-sm font-medium text-card-text-secondary">
        {t('common.labels.step', { current: currentStep, total: totalSteps })}
      </div>

      {/* Step markers */}
      <div className="flex items-center justify-center gap-2 md:gap-4">
        {Array.from({ length: totalSteps }, (_, index) => {
          const step = index + 1
          const isCompleted = step < currentStep
          const isActive = step === currentStep

          return (
            <React.Fragment key={step}>
              <div
                className={`
                  flex items-center justify-center
                  w-8 h-8 md:w-10 md:h-10
                  rounded-full
                  font-semibold text-sm
                  transition-colors duration-300
                  ${
                    isCompleted
                      ? 'bg-primary text-surface'
                      : isActive
                        ? 'bg-surface text-ds-text'
                        : 'bg-gray-200 text-card-text-secondary'
                  }
                `}
                aria-label={`Step ${step}${isCompleted ? ' completed' : isActive ? ' active' : ''}`}
                aria-current={isActive ? 'step' : undefined}
              >
                {isCompleted ? (
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  step
                )}
              </div>

              {step < totalSteps && (
                <div
                  className={`
                    h-1 w-8 md:w-16
                    transition-colors duration-300
                    ${isCompleted ? 'bg-primary' : 'bg-gray-200'}
                  `}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
